import { type FC, useMemo } from 'react';

import {
  ArrowsRightLeftIcon,
  ClockIcon,
  ExclamationCircleIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { JSONContent } from '@tiptap/core';

import { extractTiptapText } from '../../../utils/extractTiptapText';

import {
  type CommunityScheduledPostAction,
  communityScheduledPostActions,
  communityScheduledPostProblem,
} from '../../communityScheduledPostActions';
import { communityScheduledPostDestinationLabel } from '../../communityScheduledPostDestinationLabel';
import { MemberAvatar } from '../MemberAvatar';
import type { CommunitySchedulePickerTranslate } from '../schedulePicker/SchedulePickerFrame';
import { ScheduledQueueMenu, type ScheduledQueueMenuItem } from './ScheduledQueueMenu';

// The fields the queue shows, shared by the scheduled posts of the Orchid and the dashboard APIs.
export interface CommunityScheduledQueuePost {
  id: string;
  title: string | null;
  scheduled_at: string;
  status: 'scheduled' | 'publishing' | 'published' | 'failed';
  failure_reason: string | null;
  predicted_failure_reason: string | null;
  /** Required for a deleted or banned author, before anyone edits the post; optional for one who left the team. */
  takeover_mode: 'required' | 'optional' | null;
  tiptap_state: JSONContent | null;
  author: { id: string | null; name?: string | null; avatar_url?: string | null; roles: string[] };
  /** Null for a post to the feed. */
  channel: { name: string; deleted: boolean } | null;
}

interface ScheduledQueueRowProps {
  post: CommunityScheduledQueuePost;
  isAuthor: boolean;
  isAdmin: boolean;
  locale: string;
  translate: CommunitySchedulePickerTranslate;
  onEdit: () => void;
  onReschedule: () => void;
  onPublishNow: () => void;
  onTakeOver: () => void;
  onDelete: () => void;
}

const KEY = 'community.composer.schedule.queue';

// Highest first, so a member with several roles shows the one that matters most.
const ROLE_RANK = ['admin', 'manager', 'moderator'] as const;

export const ScheduledQueueRow: FC<ScheduledQueueRowProps> = ({
  post,
  isAuthor,
  isAdmin,
  locale,
  translate,
  onEdit,
  onReschedule,
  onPublishNow,
  onTakeOver,
  onDelete,
}) => {
  const name = post.author.name || translate('community.join.former_member');
  const role = ROLE_RANK.find((rank) => post.author.roles.includes(rank));
  const problem = communityScheduledPostProblem(post);
  const excerpt = useMemo(() => extractTiptapText(post.tiptap_state), [post.tiptap_state]);
  // The day heading above the row carries the date.
  const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(
    new Date(post.scheduled_at),
  );

  const items: Record<CommunityScheduledPostAction, ScheduledQueueMenuItem> = {
    edit: { label: translate('community.action_menu.edit_post'), Icon: PencilSquareIcon, onSelect: onEdit },
    reschedule: { label: translate(`${KEY}.reschedule`), Icon: ClockIcon, onSelect: onReschedule },
    publish: { label: translate(`${KEY}.publish_now`), Icon: PaperAirplaneIcon, onSelect: onPublishNow },
    takeover: { label: translate(`${KEY}.takeover_confirm`), Icon: ArrowsRightLeftIcon, onSelect: onTakeOver },
    delete: {
      label: translate('community.action_menu.delete_post'),
      Icon: TrashIcon,
      onSelect: onDelete,
      isDanger: true,
    },
  };
  const actions = communityScheduledPostActions(post, { isAuthor, isAdmin });
  const actionItems = actions.map(({ action, isDisabled }) =>
    isDisabled ? { ...items[action], onSelect: undefined, isDisabled } : items[action],
  );
  const menuItems = actions.some(({ action }) => action === 'edit')
    ? actionItems
    : [{ label: translate(`${KEY}.only_author_or_admin`) }, ...actionItems];

  return (
    <article className="flex gap-3 rounded-2xl border border-black/10 p-4">
      <MemberAvatar name={name} avatarUrl={post.author.avatar_url} />
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="flex min-w-0 items-center gap-1 text-[13px] leading-4 text-black/50">
          <span className="truncate font-medium text-black">{isAuthor ? translate(`${KEY}.you`) : name}</span>
          {role ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="shrink-0">{translate(`community.roles.${role}`)}</span>
            </>
          ) : null}
          <span aria-hidden="true">·</span>
          <span className="truncate">{communityScheduledPostDestinationLabel(post.channel, translate)}</span>
        </p>
        {post.title ? (
          <p className="mt-1 truncate text-[15px] font-semibold leading-5 text-black">{post.title}</p>
        ) : null}
        {excerpt ? <p className="truncate text-[13px] leading-4 text-black/50">{excerpt}</p> : null}
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] leading-4">
          <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-black/5 px-3 text-black/70">
            <ClockIcon className="size-4" aria-hidden="true" />
            {time}
          </span>
          {problem ? (
            <span className="flex gap-1 text-red-600">
              <ExclamationCircleIcon className="size-4 shrink-0" aria-hidden="true" />
              {/* A reason that wraps to a line of its own starts left of the clip, which hides its separator. */}
              <span className="flex min-w-0 flex-wrap overflow-hidden">
                <span className="pr-3 font-medium">{translate(`${KEY}.${problem.kind}`)}</span>
                <span className="-ml-3 flex">
                  <span className="w-3 shrink-0 text-center" aria-hidden="true">
                    ·
                  </span>
                  <span>{translate(`${KEY}.failure_reason.${problem.reason}`)}</span>
                </span>
              </span>
            </span>
          ) : null}
        </div>
      </div>
      <ScheduledQueueMenu label={translate('community.action_menu.actions')} items={menuItems} />
    </article>
  );
};
